import { useSite } from "@/lib/SiteContext";
import { useT } from "@/lib/i18n";
import Reveal from "./Reveal";

export const Diferencial = () => {
  const { locale } = useSite();
  const { t } = useT(locale);
  const points = t("diferencial.points");
  const metrics = t("diferencial.metrics");

  return (
    <section
      id="diferencial"
      className="relative py-28 md:py-40 overflow-hidden ix-grid-bg"
      style={{ backgroundColor: "var(--ix-night)", color: "var(--ix-cream)" }}
      data-testid="diferencial-section"
    >
      {/* huge background watermark */}
      <div
        aria-hidden
        className="absolute -bottom-20 -left-20 ix-display select-none pointer-events-none opacity-20"
        style={{
          fontSize: "clamp(14rem, 38vw, 40rem)",
          letterSpacing: "-0.05em",
          fontWeight: 700,
          lineHeight: 0.78,
          color: "rgba(252, 180, 36, 0.18)",
        }}
      >
        LAB
      </div>

      <div className="max-w-[1480px] mx-auto px-5 md:px-10 lg:px-14 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="ix-mono text-[0.7rem] uppercase tracking-[0.18em] flex items-center gap-2">
                <span className="ix-grad-text font-semibold">◆</span>
                {t("diferencial.eyebrow")}
              </div>
            </Reveal>

            <Reveal delay={120}>
              <h2 className="ix-display ix-headline-lg mt-7 text-[var(--ix-cream)]">
                <span className="ix-grad-text">{t("diferencial.title")}</span>
              </h2>
            </Reveal>

            <Reveal delay={220}>
              <p className="mt-8 text-[1.05rem] leading-relaxed text-[var(--ix-cream)]/85 max-w-md">
                {t("diferencial.lead")}
              </p>
            </Reveal>

            <Reveal delay={320}>
              <p className="mt-5 text-[0.95rem] leading-relaxed text-[var(--ix-cream)]/55 max-w-md">
                {t("diferencial.body")}
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            {/* Massive metrics */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {metrics.map((m, i) => (
                <Reveal
                  key={m.label}
                  delay={i * 100}
                  className="relative p-7 md:p-9 border border-[var(--ix-cream)]/12 ix-sweep"
                  style={{ background: "rgba(17, 21, 43, 0.55)" }}
                  data-testid={`metric-${i}`}
                >
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <div className="ix-display text-[3.6rem] md:text-[4.6rem] leading-none ix-grad-text">
                      {m.value}
                    </div>
                    {m.unit && (
                      <div className="ix-display text-[1.05rem] md:text-[1.2rem] text-[var(--ix-cream)]/60" style={{ fontWeight: 500 }}>
                        {m.unit}
                      </div>
                    )}
                  </div>
                  <div className="mt-4 ix-mono text-[0.72rem] uppercase tracking-[0.12em] text-[var(--ix-cream)]/65 leading-relaxed">
                    {m.label}
                  </div>
                </Reveal>
              ))}
            </div>

            <div className="mt-12">
              <Reveal>
                <div className="ix-mono text-[0.7rem] uppercase tracking-[0.16em] text-[var(--ix-cream)]/55 mb-5 flex items-center gap-3">
                  <span>{locale === "es" ? "Lo que nos hace diferentes" : "What makes us different"}</span>
                  <span className="flex-1 h-px bg-[var(--ix-cream)]/15" />
                </div>
              </Reveal>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3">
                {points.map((p, i) => (
                  <Reveal
                    as="li"
                    key={p}
                    delay={i * 70}
                    className="flex items-start gap-3 text-[0.95rem] text-[var(--ix-cream)]/90"
                  >
                    <span
                      aria-hidden
                      style={{
                        width: 10, height: 13, marginTop: 5,
                        background: "linear-gradient(135deg, #FF1F8F, #8AE52E)",
                        clipPath: "polygon(22% 0%, 100% 0%, 78% 100%, 0% 100%)",
                        flexShrink: 0,
                      }}
                    />
                    {p}
                  </Reveal>
                ))}
              </ul>
            </div>

            {/* Clients social proof */}
            {t("diferencial.clients") && Array.isArray(t("diferencial.clients")) && (
              <div className="mt-12">
                <Reveal>
                  <div className="ix-mono text-[0.7rem] uppercase tracking-[0.16em] text-[var(--ix-cream)]/55 mb-5 flex items-center gap-3">
                    <span>{t("diferencial.clients_eyebrow")}</span>
                    <span className="flex-1 h-px bg-[var(--ix-cream)]/15" />
                  </div>
                </Reveal>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-[var(--ix-cream)]/12">
                  {t("diferencial.clients").map((c, i) => (
                    <Reveal
                      key={c}
                      delay={i * 70}
                      className="p-5 md:p-6 flex items-center justify-center text-center min-h-[88px]"
                      style={{ background: "rgba(17, 21, 43, 0.55)" }}
                      data-testid={`client-${i}`}
                    >
                      <span className="ix-display text-[0.95rem] md:text-[1.05rem] leading-tight text-[var(--ix-cream)]/90" style={{ fontWeight: 500 }}>
                        {c}
                      </span>
                    </Reveal>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Diferencial;
